import { useEffect, useState } from "react";
import { Reveal } from "./Reveal";
import { ChevronLeft, ChevronRight, X, Image as ImageIcon } from "lucide-react";

/* Wiederverwendbare Galerie: 9 Slots, 4 pro Reihe, Lightbox mit Pfeil-Navigation.
   Platzhalter-Slots (src: null) zeigen eine „Bild folgt"-Kachel. */
export const Gallery = ({ gallery, gi, testidPrefix = "gallery", heading, overline = "Galerie" }) => {
  const [open, setOpen] = useState(null);
  const count = gallery.images.length;
  const prev = () => setOpen((o) => (o - 1 + count) % count);
  const next = () => setOpen((o) => (o + 1) % count);

  useEffect(() => {
    if (open === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(null);
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open !== null]);

  const current = open !== null ? gallery.images[open] : null;

  return (
    <section data-testid={`${testidPrefix}-gallery-${gi}`} className="pb-20 lg:pb-28">
      <div className="container-blum">
        {heading ? (
          <>
            <Reveal><p className="overline">{overline}</p></Reveal>
            <Reveal delay={0.05}>
              <h2 className="text-3xl lg:text-4xl font-bold tracking-tight mt-4 mb-8 leading-tight">{heading}</h2>
            </Reveal>
          </>
        ) : (
          <Reveal><p className="overline mb-8">{gallery.label}</p></Reveal>
        )}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {gallery.images.map((img, i) => (
            <Reveal key={i} delay={(i % 4) * 0.05}>
              <button
                data-testid={`${testidPrefix}-img-${gi}-${i}`}
                onClick={() => setOpen(i)}
                aria-label={`${img.alt} vergrößern`}
                className="group relative aspect-[4/3] w-full overflow-hidden bg-[#EDE7DE] flex flex-col items-center justify-center gap-2 text-[#8B8578] hover:bg-[#D7C4B1]/50 transition-colors"
              >
                {img.src ? (
                  <img
                    src={img.src}
                    alt={img.alt}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                ) : (
                  <>
                    <ImageIcon className="w-6 h-6" />
                    <span className="text-xs font-display uppercase tracking-[0.12em]">
                      Bild folgt
                    </span>
                  </>
                )}
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      {open !== null && (
        <div
          data-testid="lightbox"
          className="fixed inset-0 z-[100] bg-[#2B2B30]/95 flex items-center justify-center p-4 sm:p-10"
          onClick={() => setOpen(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Bildergalerie"
        >
          <button
            data-testid="lightbox-close"
            onClick={() => setOpen(null)}
            aria-label="Schließen"
            className="absolute top-5 right-5 w-11 h-11 flex items-center justify-center text-white/70 hover:text-white border border-white/20 hover:border-white/50 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <button
            data-testid="lightbox-prev"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            aria-label="Vorheriges Bild"
            className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center text-white/70 hover:text-white border border-white/20 hover:border-white/50 transition-colors"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <div
            className="max-w-4xl w-full flex flex-col items-center gap-4"
            onClick={(e) => e.stopPropagation()}
          >
            {current.src ? (
              <img src={current.src} alt={current.alt} className="max-h-[75vh] w-auto max-w-full object-contain" />
            ) : (
              <div className="w-full aspect-[4/3] max-h-[75vh] bg-[#EDE7DE] flex flex-col items-center justify-center gap-3 text-[#8B8578]">
                <ImageIcon className="w-10 h-10" />
                <span className="text-sm font-display uppercase tracking-[0.12em]">Bild folgt</span>
              </div>
            )}
            <p className="text-white/60 text-sm font-display tracking-wider">
              {open + 1} / {count}
            </p>
          </div>
          <button
            data-testid="lightbox-next"
            onClick={(e) => { e.stopPropagation(); next(); }}
            aria-label="Nächstes Bild"
            className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center text-white/70 hover:text-white border border-white/20 hover:border-white/50 transition-colors"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>
      )}
    </section>
  );
};
